/**
 * One description of a column, used by everything that needs one.
 *
 * The column list used to exist twice: a `ColumnDef[]` for the picker, and a
 * hand-maintained index→key array inside the CSV shaper, positionally aligned
 * with a header array three lines below it. Add or reorder a column and the
 * file silently exported the wrong values under the right headings — not a
 * crash, just quietly wrong data.
 *
 * So a column now says once what it is: how to DRAW it, how to READ it (for
 * the file and for sorting), and how wide it wants to be. The table body, the
 * picker, the download and the sort all derive from the same list.
 */
import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  /** Relative width. Ratios, not a total anyone has to keep adding up. */
  width?: number;
  align?: 'left' | 'right';
  /** A column the table is keyed by; offered in the picker but never hidden. */
  always?: boolean;
  /** Off until the reader turns it on in the picker. */
  hiddenByDefault?: boolean;
  /** What the reader sees. Falls back to `value` when a plain string will do. */
  cell?: (row: T) => ReactNode;
  /** What the FILE gets and what SORT compares. The single source of truth
   *  for what this column means. */
  value: (row: T) => string | number | null;
  /** Chip lists and the like: real content, but nothing to order by. */
  sortable?: boolean;
  /** A drawn header, where the label alone will not do: a product button, a
   *  title with the count that qualifies it. `label` still names the column in
   *  the picker and in the file, so the accessible name stays a plain string. */
  head?: ReactNode;
  /** The plain text a DRAWN header actually renders, when that is not `label`.
   *  Sideways headers get an explicit band height measured off a string, and
   *  measuring `label` while rendering `head` sized every band too short by
   *  exactly the part `head` adds — which is the count, at the clipped end. */
  measure?: string;
  /** The cell's own test id. Written as a function of the ROW so it composes
   *  the same either way round: a matrix cell is `cell-<feature>-<product>`
   *  whether features are down the side or across the top. */
  cellId?: (row: T) => string;
  /** Per-cell tone — a count nobody has verified, a margin below zero. Row
   *  tone is `rowClass`; this is for when one cell is the finding. */
  cellClass?: (row: T) => string;
  /** Alignment that belongs to the ROW rather than the column. Only the flip
   *  needs it: a right-aligned price column becomes a right-aligned price ROW,
   *  and alignment has to travel with the field it describes. */
  cellAlign?: (row: T) => 'left' | 'right' | undefined;
  /** The item this column stands for, where it stands for one — a product
   *  column of a matrix. Flipped, that column becomes a ROW, and a row of a
   *  real item is a row that can carry an unread mark. */
  itemId?: number;
  /** A header written up the side rather than across. For a grid with a
   *  column per product there is no room to write names across the top, and
   *  the summary columns beside them still have room — so it is per column,
   *  not per table. */
  vertical?: boolean;
  /** FIRST COLUMN ONLY: how wide the column a given ROW becomes should be,
   *  once flipped. A spec sheet already records what each attribute IS, so a
   *  boolean gets 5 and a paragraph 18 — but only in the orientation where the
   *  attribute is a column, and nothing but the row knows its kind. */
  rowWidth?: (row: T) => number;
  /** FIRST COLUMN ONLY: what this column is called once the table is flipped.
   *  It names the other columns' kind — "compared on" going down becomes
   *  "product" going across — and nothing else can know that. */
  flipLabel?: string;
}

export type SortDir = 'asc' | 'desc';
export interface Sort {
  key: string;
  dir: SortDir;
}

/** Click a column: ascending, then descending, then back to the board's own
 *  order — which is meaningful here (oldest first, or what the report ranked)
 *  and worth being able to get back to. */
export function nextSort(cur: Sort | null, key: string): Sort | null {
  if (!cur || cur.key !== key) return { key, dir: 'asc' };
  if (cur.dir === 'asc') return { key, dir: 'desc' };
  return null;
}

/** Blank last, whichever way you are sorting: an empty cell is missing
 *  information, not a small value, and burying the rows you have under the
 *  rows you do not is never what anybody meant. */
export function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows<T>(rows: T[], columns: Column<T>[], sort: Sort | null): T[] {
  if (!sort) return rows;
  const col = columns.find(c => c.key === sort.key);
  if (!col) return rows;
  const out = rows.slice().sort((x, y) => compareValues(col.value(x), col.value(y)));
  if (sort.dir === 'desc') {
    // Reverse the ORDERED rows only, so the blanks stay at the bottom rather
    // than rising to the top the moment you look at it the other way round.
    const blanks = out.filter(r => {
      const v = col.value(r);
      return v === null || v === undefined || v === '';
    });
    const filled = out.filter(r => !blanks.includes(r));
    return [...filled.reverse(), ...blanks];
  }
  return out;
}

/** Rows kept together by `section`, groups in the order they first appear,
 *  each group in `rows`' own order. Pure.
 *
 *  SORTING MUST NOT SCATTER A GROUP. A table drawn in bands (a company's
 *  pains, the table-stakes features) sorted by a column across the whole
 *  table, and every band broke into pieces with a heading on each. */
export function keepGroups<T>(rows: T[], groupOrder: T[], section: (row: T) => string | null): T[] {
  const rank = new Map<string | null, number>();
  for (const r of groupOrder) {
    const s = section(r);
    if (!rank.has(s)) rank.set(s, rank.size);
  }
  const at = new Map(rows.map((r, i) => [r, i]));
  return rows
    .slice()
    .sort((a, b) => (rank.get(section(a)) ?? 0) - (rank.get(section(b)) ?? 0) || (at.get(a) ?? 0) - (at.get(b) ?? 0));
}

/** The rows a reader can see, in the order they are reading them — which is
 *  exactly what the download should carry. */
export function csvRows<T>(
  columns: Column<T>[],
  rows: T[],
): (string | number | null)[][] {
  return [columns.map(c => c.label), ...rows.map(r => columns.map(c => c.value(r)))];
}
